"use client";

import React, { useState, useEffect } from "react";
import { Menu, MessageCircle, X } from "lucide-react";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import RichiediUnaConsulenzaGratuita from "@/pages/Home/sections/RichiediUnaConsulenzaGratuita";
import { cn } from "@/lib/utils";

// "Calcola il costo" apre la pagina dedicata /calcola-il-costo/
const NAV_ANCHORS = [{
  label: "Prezzi",
  to: "#analisi-comparativa-dei-costi"
}, {
  label: "Calcola il costo",
  to: "https://impiantidentali.co/calcola-il-costo/"
}, {
  label: "Percorso",
  to: "#il-percorso-del-paziente-in-3-fasi"
}, {
  label: "Clinica",
  to: "#clinica-raccomandata-skydental-tirana"
}, {
  label: "FAQ",
  to: "#domande-frequenti-e-testimonianze"
}];
function MobileMenuConsumer({
  onSelect
}: {
  onSelect?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col items-center gap-4 py-2">
        {[1, 2].map((_, i) => <div key={i} data-index={i} className="h-6 w-36 animate-pulse rounded-md bg-muted/50" />)}
      </div>;
  }
  return <div className="flex flex-col items-center gap-4 py-2">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onSelect} className="font-serif text-2xl font-medium text-foreground transition-colors hover:text-primary">
          {item.label}
        </Link>)}
    </div>;
}
export default function FraternalExcellenceHeader() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const handleScrollToAnchor = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({
        behavior: "smooth"
      });
    }
  };
  const handleMobileAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsMenuOpen(false);
    setTimeout(() => {
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({
          behavior: "smooth"
        });
      }
    }, 250);
  };
  const handleContactClick = () => {
    const target = document.querySelector("#richiedi-una-consulenza-gratuita");
    if (target) {
      target.scrollIntoView({
        behavior: "smooth"
      });
    }
  };
  const handleMobileContactClick = () => {
    setIsMenuOpen(false);
    setIsModalOpen(true);
    setTimeout(() => {
      document.querySelector("#richiedi-una-consulenza-gratuita")?.scrollIntoView({
        behavior: "smooth"
      });
    }, 300);
  };
  return <>
      <header data-section-id="252277" id="header" className={cn("fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 animate-header-intro", isScrolled ? "bg-[#0f172a]/90 backdrop-blur-md shadow-md" : "bg-[#0f172a]")}>
        <style dangerouslySetInnerHTML={{
        __html: `
              @keyframes header-intro {
                0% { opacity: 0; transform: translateY(-10px); }
                100% { opacity: 1; transform: translateY(0); }
              }
              .animate-header-intro {
                animation: header-intro 0.6s ease-out forwards;
              }
            `
      }} />

        <div className={cn("container mx-auto flex items-center justify-between px-4 transition-all duration-300 md:px-6 lg:px-8", isScrolled ? "h-[72px]" : "h-24 lg:h-[104px]")}>
          {/* Logo Mark */}
          <Link to="/" className="group flex items-center gap-2.5 transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md" aria-label="ImpiantiDentali.co Homepage">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary font-sans text-xs font-semibold text-white transition-transform duration-300 group-hover:scale-105 select-none" aria-hidden="true">
              ID
            </span>
            <span className="font-default text-xl font-bold tracking-tight text-white">
              ImpiantiDentali<span className="text-primary font-bold">.co</span>
            </span>
          </Link>

          {/* Center Navigation - Desktop Only */}
          <nav aria-label="Main Navigation" className="hidden items-center gap-7 lg:flex xl:gap-8">
            {NAV_ANCHORS.map((item, i) => <Link key={item.to} data-index={i} to={item.to} onClick={e => {
            if (item.to.startsWith("#")) handleScrollToAnchor(e, item.to);
          }} className="relative py-1 font-default text-sm font-medium tracking-wide text-white/90 transition-colors hover:text-white after:absolute after:bottom-[-2px] after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
                {item.label}
              </Link>)}
            <Link to="/AboutUsPage" className="relative py-1 font-default text-sm font-medium tracking-wide text-white/90 transition-colors hover:text-white after:absolute after:bottom-[-2px] after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
              Chi Siamo
            </Link>
          </nav>

          {/* Right Action Button & Mobile Hamburger */}
          <div className="flex items-center gap-3">
            {/* Desktop Pill CTA */}
            <div className="hidden md:block">
              <Button variant="default" className="rounded-full bg-primary px-6 py-2.5 font-default text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-primary/90 hover:shadow-md focus-visible:ring-2 focus-visible:ring-primary" onClick={handleContactClick}>
                Piano scritto in 24 ore
              </Button>
            </div>

            {/* Mobile Menu Trigger */}
            <div className="lg:hidden">
              <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 hover:text-primary focus-visible:ring-2 focus-visible:ring-primary" onClick={() => setIsMenuOpen(true)}>
                    <Menu className="size-6" />
                    <span className="sr-only">Apri menu di navigazione</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="flex h-screen w-screen max-w-none flex-col justify-between border-0 bg-background/98 p-6 backdrop-blur-xl sm:max-w-none">
                  <SheetHeader className="flex flex-row items-center justify-between border-b pb-4">
                    <SheetTitle className="font-default text-lg font-bold tracking-tight text-foreground">
                      ImpiantiDentali<span className="text-primary">.co</span>
                    </SheetTitle>
                  </SheetHeader>

                  {/* Centered Navigation Links */}
                  <div className="my-auto flex flex-col items-center justify-center gap-5 text-center">
                    {NAV_ANCHORS.map((item, idx) => <Link key={item.to} data-index={idx} to={item.to} onClick={e => {
                    if (item.to.startsWith("#")) handleMobileAnchorClick(e, item.to);else setIsMenuOpen(false);
                  }} className="font-serif text-2xl font-medium text-foreground transition-colors hover:text-primary">
                        {item.label}
                      </Link>)}
                    <Link to="/AboutUsPage" onClick={() => setIsMenuOpen(false)} className="font-serif text-2xl font-medium text-foreground transition-colors hover:text-primary">
                      Chi Siamo
                    </Link>
                    <MenuProvider menu_id="50">
                      <MobileMenuConsumer onSelect={() => setIsMenuOpen(false)} />
                    </MenuProvider>
                  </div>

                  {/* Bottom Full-Screen Pill CTA */}
                  <div className="mt-auto border-t pt-6">
                    <Button className="w-full rounded-full bg-primary py-6 text-base font-semibold text-primary-foreground shadow-md transition-all hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-primary" onClick={handleMobileContactClick}>
                      Piano scritto in 24 ore
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        {/* Modal Consultation Form */}
        {isModalOpen && <div className="fixed inset-0 z-[50] flex items-center justify-center p-4 md:hidden">
            <div className="absolute inset-0 z-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsModalOpen(false)} />
            <div className="relative h-screen w-full max-w-lg overflow-y-auto rounded-lg bg-background p-6 shadow-2xl">
              <Button variant="ghost" size="icon" className="absolute right-2 top-2 text-muted-foreground hover:bg-accent hover:text-primary focus-visible:ring-2 focus-visible:ring-primary" onClick={() => setIsModalOpen(false)}>
                <X className="size-6" />
                <span className="sr-only">Chiudi</span>
              </Button>
              <div className="mt-4">
                <RichiediUnaConsulenzaGratuita form_id="consultation_form_home" isModal={true} />
              </div>
            </div>
          </div>}
      </header>

      {/* Floating WhatsApp Action Button */}
      <Link to="https://wa.me/393892866221?text=Buongiorno%2C%20vorrei%20ricevere%20il%20piano%20scritto%20per%20un%20impianto%20dentale.%20%5BIDCO-IT-PLAN-SKY%5D" newTab aria-label="Scrivici su WhatsApp" className="fixed bottom-6 right-6 z-[100] flex size-[52px] items-center justify-center rounded-full bg-[#25D366] shadow-lg transition-transform duration-300 hover:scale-105 hover:bg-[#20ba5a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:size-14">
        <MessageCircle className="size-7 text-white" aria-hidden="true" />
      </Link>
    </>;
}